import React, { useState, useEffect } from 'react';

const FullEditor = () => {
  const [authService] = useState(() => new window.CanopyAuthService());
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Content states
  const [activeSection, setActiveSection] = useState('team');
  const [teamData, setTeamData] = useState({
    founder: [],
    directors: [],
    advisors: [],
    members: []
  });
  const [charityData, setCharityData] = useState([]);
  const [eventsData, setEventsData] = useState([]);
  const [statisticsData, setStatisticsData] = useState({
    homepage: {
      teamMembers: '7+',
      hoursStreamed: '13,000+',
      charityRaised: '$10K+',
      communityEvents: 'Weekly'
    },
    charity: {
      totalCharityGoal: '$12,000',
      nextEventGoal: '$5,000',
      eventDescription: 'Holiday Charity Stream Marathon',
      donationLink: ''
    },
    team: {
      membershipStatus: 'enabled'
    }
  });

  // Form states
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    checkAuthentication();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadCurrentData();
    }
  }, [isAuthenticated, activeSection]);

  const checkAuthentication = async () => {
    if (authService.isAuthenticated()) {
      const result = await authService.verifyToken();
      if (result.valid) {
        setIsAuthenticated(true);
        setUser(result.user);
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    }
  };

  const loadCurrentData = async () => {
    setLoading(true);
    const result = await authService.getCurrentContent(activeSection);

    if (result.success) {
      switch (activeSection) {
        case 'team':
          // Ensure teamData has the correct structure
          const teamDataStructure = result.data || {};
          setTeamData({
            founder: Array.isArray(teamDataStructure.founder) ? teamDataStructure.founder : [],
            directors: Array.isArray(teamDataStructure.directors) ? teamDataStructure.directors : [],
            advisors: Array.isArray(teamDataStructure.advisors) ? teamDataStructure.advisors : [],
            members: Array.isArray(teamDataStructure.members) ? teamDataStructure.members : []
          });
          break;
        case 'charity':
          setCharityData(Array.isArray(result.data) ? result.data : []);
          break;
        case 'events':
          setEventsData(Array.isArray(result.data) ? result.data : []);
          break;
        case 'statistics':
          setStatisticsData(result.data || {
            homepage: {
              teamMembers: '7+',
              hoursStreamed: '13,000+',
              charityRaised: '$10K+',
              communityEvents: 'Weekly'
            },
            charity: {
              totalCharityGoal: '$12,000',
              nextEventGoal: '$5,000',
              eventDescription: 'Holiday Charity Stream Marathon',
              donationLink: ''
            },
            team: {
              membershipStatus: 'enabled'
            }
          });
          break;
      }
    } else {
      showMessage(`Failed to load ${activeSection} data: ${result.error}`, 'error');
      // Set default data on error
      if (activeSection === 'team') {
        setTeamData({
          founder: [],
          directors: [],
          advisors: [],
          members: []
        });
      }
    }
    setLoading(false);
  };

  const downloadCurrentData = async () => {
    const result = await authService.downloadContent(activeSection);
    
    if (result.success) {
      showMessage(`Downloaded ${result.filename} successfully!`);
    } else {
      showMessage(`Failed to download: ${result.error}`, 'error');
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(''), 5000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await authService.login(loginData.username, loginData.password);
    
    if (result.success) {
      setIsAuthenticated(true);
      setUser(result.user);
      showMessage(`Welcome ${result.user.username}! You have ${result.user.roles.join(', ')} permissions.`);
      setLoginData({ username: '', password: '' });
    } else {
      showMessage(result.error, 'error');
    }

    setLoading(false);
  };

  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setUser(null);
    showMessage('Logged out successfully');
  };

  const handleSaveAll = async () => {
    setLoading(true);

    let data, contentType;
    switch (activeSection) {
      case 'team':
        data = teamData;
        contentType = 'team';
        break;
      case 'charity':
        data = charityData;
        contentType = 'charity';
        break;
      case 'events':
        data = eventsData;
        contentType = 'events';
        break;
      case 'statistics':
        data = statisticsData;
        contentType = 'statistics';
        break;
      default:
        showMessage('Unknown content type', 'error');
        setLoading(false);
        return;
    }

    const result = await authService.updateContent(contentType, data);
    
    if (result.success) {
      showMessage(`${contentType} content saved successfully! Updated by: ${result.data.updatedBy}`);
    } else {
      showMessage(`Failed to save ${contentType}: ${result.error}`, 'error');
    }

    setLoading(false);
  };


  // CRUD operations for statistics
  const updateStatistics = (statisticsUpdate) => {
    const errors = validateStatistics(statisticsUpdate);
    if (Object.keys(errors).length > 0) {
      showMessage(`Validation errors: ${Object.values(errors).join(', ')}`, 'error');
      return false;
    }

    setStatisticsData(statisticsUpdate);
    showMessage('Statistics updated successfully');
    return true;
  };

  const validateStatistics = (stats) => {
    const errors = {};
    
    // Validate homepage statistics
    if (!stats.homepage?.teamMembers || stats.homepage.teamMembers.length < 1) {
      errors.teamMembers = 'Team members statistic is required';
    }
    if (!stats.homepage?.hoursStreamed || stats.homepage.hoursStreamed.length < 1) {
      errors.hoursStreamed = 'Hours streamed statistic is required';
    }
    if (!stats.homepage?.charityRaised || stats.homepage.charityRaised.length < 1) {
      errors.charityRaised = 'Charity raised statistic is required';
    }
    if (!stats.homepage?.communityEvents || stats.homepage.communityEvents.length < 1) {
      errors.communityEvents = 'Community events statistic is required';
    }
    
    // Validate charity statistics
    if (!stats.charity?.totalCharityGoal || stats.charity.totalCharityGoal.length < 1) {
      errors.totalCharityGoal = 'Total charity goal is required';
    }
    if (!stats.charity?.nextEventGoal || stats.charity.nextEventGoal.length < 1) {
      errors.nextEventGoal = 'Next event goal is required';
    }
    if (!stats.charity?.eventDescription || stats.charity.eventDescription.length < 3) {
      errors.eventDescription = 'Event description must be at least 3 characters';
    }
    
    return errors;
  };

  // Form validation helpers
  const validateTeamMember = (member) => {
    const errors = {};
    if (!member.twitch || member.twitch.length < 2) {
      errors.twitch = 'Twitch username must be at least 2 characters';
    }
    if (!/^[a-zA-Z0-9_]{2,25}$/.test(member.twitch)) {
      errors.twitch = 'Twitch username can only contain letters, numbers, and underscores';
    }
    if (!member.display_name || member.display_name.length < 2) {
      errors.display_name = 'Display name must be at least 2 characters';
    }
    if (member.website && member.website.length > 0) {
      try {
        new URL(member.website);
        if (!member.website.startsWith('http://') && !member.website.startsWith('https://')) {
          errors.website = 'Website must start with http:// or https://';
        }
      } catch {
        errors.website = 'Please enter a valid URL';
      }
    }
    return errors;
  };

  const validateCharity = (charity) => {
    const errors = {};
    if (!charity.name || charity.name.length < 2) {
      errors.name = 'Organization name must be at least 2 characters';
    }
    if (!charity.mission || charity.mission.length < 10) {
      errors.mission = 'Mission statement must be at least 10 characters';
    }
    if (!charity.description || charity.description.length < 20) {
      errors.description = 'Description must be at least 20 characters';
    }
    if (charity.website) {
      try {
        new URL(charity.website);
      } catch {
        errors.website = 'Please enter a valid URL';
      }
    }
    if (charity.amountRaised < 0) {
      errors.amountRaised = 'Amount raised cannot be negative';
    }
    return errors;
  };

  const validateEvent = (event) => {
    const errors = {};
    if (!event.title || event.title.length < 3) {
      errors.title = 'Title must be at least 3 characters';
    }
    if (!event.description || event.description.length < 10) {
      errors.description = 'Description must be at least 10 characters';
    }
    if (!event.host || event.host.length < 2) {
      errors.host = 'Host must be at least 2 characters';
    }
    if (!event.participants || event.participants.length < 2) {
      errors.participants = 'Participants must be at least 2 characters';
    }
    if (!event.startDate) {
      errors.startDate = 'Start date is required';
    }
    if (event.startDate && event.endDate) {
      const start = new Date(event.startDate);
      const end = new Date(event.endDate);
      if (end < start) {
        errors.endDate = 'End date must be after start date';
      }
    }
    if (event.link && event.link.length > 0) {
      try {
        new URL(event.link);
      } catch {
        errors.link = 'Please enter a valid URL';
      }
    }
    return errors;
  };

  // CRUD operations for team members
  const addTeamMember = (category, member) => {
    const errors = validateTeamMember(member);
    if (Object.keys(errors).length > 0) {
      showMessage(`Validation errors: ${Object.values(errors).join(', ')}`, 'error');
      return false;
    }

    setTeamData(prev => ({
      ...prev,
      [category]: [...prev[category], { ...member, twitch: member.twitch.toLowerCase() }]
    }));
    showMessage(`Added ${member.display_name} to ${category}`);
    return true;
  };

  const updateTeamMember = (category, index, member) => {
    const errors = validateTeamMember(member);
    if (Object.keys(errors).length > 0) {
      showMessage(`Validation errors: ${Object.values(errors).join(', ')}`, 'error');
      return false;
    }

    setTeamData(prev => ({
      ...prev,
      [category]: prev[category].map((item, i) => 
        i === index ? { ...member, twitch: member.twitch.toLowerCase() } : item
      )
    }));
    showMessage(`Updated ${member.display_name}`);
    return true;
  };

  const deleteTeamMember = (category, index) => {
    const member = teamData[category][index];
    setTeamData(prev => ({
      ...prev,
      [category]: prev[category].filter((_, i) => i !== index)
    }));
    showMessage(`Removed ${member.display_name} from ${category}`);
  };

  // CRUD operations for charities
  const addCharity = (charity) => {
    const errors = validateCharity(charity);
    if (Object.keys(errors).length > 0) {
      showMessage(`Validation errors: ${Object.values(errors).join(', ')}`, 'error');
      return false;
    }

    setCharityData(prev => [...prev, { ...charity, logo: null }]);
    showMessage(`Added ${charity.name} charity`);
    return true;
  };

  const updateCharity = (index, charity) => {
    const errors = validateCharity(charity);
    if (Object.keys(errors).length > 0) {
      showMessage(`Validation errors: ${Object.values(errors).join(', ')}`, 'error');
      return false;
    }

    setCharityData(prev => prev.map((item, i) => 
      i === index ? { ...charity, logo: null } : item
    ));
    showMessage(`Updated ${charity.name}`);
    return true;
  };

  const deleteCharity = (index) => {
    const charity = charityData[index];
    setCharityData(prev => prev.filter((_, i) => i !== index));
    showMessage(`Removed ${charity.name}`);
  };

  // CRUD operations for events
  const addEvent = (event) => {
    const errors = validateEvent(event);
    if (Object.keys(errors).length > 0) {
      showMessage(`Validation errors: ${Object.values(errors).join(', ')}`, 'error');
      return false;
    }

    // If no end date is provided, set it to 1 day after the start date
    let endDate = event.endDate;
    if (!endDate && event.startDate) {
      // Convert startDate to YYYY-MM-DD format if it's in display format
      const startDateStr = convertToDatePickerFormat(event.startDate) || event.startDate;
      if (startDateStr) {
        const [year, month, day] = startDateStr.split('-').map(num => parseInt(num));
        const startDate = new Date(year, month - 1, day);
        const nextDay = new Date(startDate);
        nextDay.setDate(startDate.getDate() + 1);

        // Format as display date to match the form data format
        const nextYear = nextDay.getFullYear();
        const nextMonth = String(nextDay.getMonth() + 1).padStart(2, '0');
        const nextDayStr = String(nextDay.getDate()).padStart(2, '0');
        endDate = formatDateForDisplay(`${nextYear}-${nextMonth}-${nextDayStr}`);
      }
    }

    const processedEvent = {
      ...event,
      endDate,
      tags: Array.isArray(event.tags) ? event.tags : (event.tags ? event.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0) : [])
    };

    setEventsData(prev => [...prev, processedEvent]);
    showMessage(`Added event: ${event.title}`);
    return true;
  };

  const updateEvent = (index, event) => {
    const errors = validateEvent(event);
    if (Object.keys(errors).length > 0) {
      showMessage(`Validation errors: ${Object.values(errors).join(', ')}`, 'error');
      return false;
    }

    // If no end date is provided, set it to 1 day after the start date
    let endDate = event.endDate;
    if (!endDate && event.startDate) {
      // Convert startDate to YYYY-MM-DD format if it's in display format
      const startDateStr = convertToDatePickerFormat(event.startDate) || event.startDate;
      if (startDateStr) {
        const [year, month, day] = startDateStr.split('-').map(num => parseInt(num));
        const startDate = new Date(year, month - 1, day);
        const nextDay = new Date(startDate);
        nextDay.setDate(startDate.getDate() + 1);

        // Format as display date to match the form data format
        const nextYear = nextDay.getFullYear();
        const nextMonth = String(nextDay.getMonth() + 1).padStart(2, '0');
        const nextDayStr = String(nextDay.getDate()).padStart(2, '0');
        endDate = formatDateForDisplay(`${nextYear}-${nextMonth}-${nextDayStr}`);
      }
    }

    const processedEvent = {
      ...event,
      endDate,
      tags: Array.isArray(event.tags) ? event.tags : (event.tags ? event.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0) : [])
    };

    setEventsData(prev => prev.map((item, i) => 
      i === index ? processedEvent : item
    ));
    showMessage(`Updated event: ${event.title}`);
    return true;
  };

  const deleteEvent = (index) => {
    const event = eventsData[index];
    setEventsData(prev => prev.filter((_, i) => i !== index));
    showMessage(`Removed event: ${event.title}`);
  };

  // Login form for unauthenticated users
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <div className="card text-center">
            <h1 className="text-4xl font-bold text-gradient mb-6">🔐 Full Editor Access</h1>
            <p className="text-forest-600 mb-6">
              Please log in to access the full content management system.
            </p>

            {message && (
              <div className={`mb-4 p-3 rounded-lg ${
                message.type === 'error' 
                  ? 'bg-red-100 text-red-800' 
                  : 'bg-green-100 text-green-800'
              }`}>
                {message.text}
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-forest-700 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  value={loginData.username}
                  onChange={(e) => setLoginData(prev => ({...prev, username: e.target.value}))}
                  className="w-full px-3 py-2 border border-forest-300 rounded-md focus:ring-forest-500 focus:border-forest-500"
                  required
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-forest-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData(prev => ({...prev, password: e.target.value}))}
                  className="w-full px-3 py-2 border border-forest-300 rounded-md focus:ring-forest-500 focus:border-forest-500"
                  required
                  disabled={loading}
                />
              </div>
              <button
                type="submit"
                className="w-full btn-enhanced btn-primary"
                disabled={loading}
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header with user info */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gradient mb-4">🌲 Full Content Editor</h1>
          <p className="text-lg text-forest-600">
            Complete content management with live editing capabilities
          </p>
          
          <div className="mt-4 flex justify-center items-center gap-4 flex-wrap">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
              <span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>
              Logged in as: <strong>{user.username}</strong>
            </div>
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
              Roles: {user.roles.join(', ')}
            </div>
            <button
              onClick={downloadCurrentData}
              className="btn-enhanced btn-outline text-sm"
              disabled={loading}
            >
              📥 Download {activeSection} Data
            </button>
            <button
              onClick={handleLogout}
              className="btn-enhanced btn-outline text-sm"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Message Display */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'error' 
              ? 'bg-red-100 text-red-800' 
              : 'bg-green-100 text-green-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => setActiveSection('team')}
              className={`btn-enhanced ${
                activeSection === 'team' ? 'btn-primary' : 'btn-outline'
              }`}
            >
              Team Members
            </button>
            <button
              onClick={() => setActiveSection('charity')}
              className={`btn-enhanced ${
                activeSection === 'charity' ? 'btn-primary' : 'btn-outline'
              }`}
            >
              Charity Partners
            </button>
            <button
              onClick={() => setActiveSection('events')}
              className={`btn-enhanced ${
                activeSection === 'events' ? 'btn-primary' : 'btn-outline'
              }`}
            >
              Events
            </button>
            <button
              onClick={() => setActiveSection('statistics')}
              className={`btn-enhanced ${
                activeSection === 'statistics' ? 'btn-primary' : 'btn-outline'
              }`}
            >
              📊 Statistics
            </button>
          </div>
        </div>

        {/* Main Action Buttons */}
        <div className="card text-center mb-8">
          <h2 className="text-2xl font-bold text-gradient mb-4">
            {activeSection.charAt(0).toUpperCase() + activeSection.slice(1)} Management
          </h2>
          
          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <h3 className="font-semibold text-blue-800 mb-2">📋 Workflow:</h3>
            <div className="text-sm text-blue-700">
              <div><strong>Save & Deploy:</strong> Saves your changes to the backend and immediately deploys to the live website</div>
              <div><strong>Backup:</strong> Previous version is automatically backed up before deploying changes</div>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => setShowAddForm(true)}
              className="btn-enhanced btn-secondary"
              disabled={loading}
            >
              ➕ Add New {activeSection === 'team' ? 'Member' : activeSection === 'charity' ? 'Charity' : 'Event'}
            </button>
            
            <button
              onClick={handleSaveAll}
              className="btn-enhanced btn-primary"
              disabled={loading}
              title="Saves changes and immediately deploys to live website with automatic backup"
            >
              {loading ? 'Saving & Deploying...' : `💾🚀 Save & Deploy ${activeSection.charAt(0).toUpperCase() + activeSection.slice(1)}`}
            </button>
          </div>
        </div>

        {/* Content listing and editing will go here */}
        <div className="card">
          <h3 className="text-xl font-bold text-forest-800 mb-4">
            Current {activeSection.charAt(0).toUpperCase() + activeSection.slice(1)} Items
            {loading && <span className="text-sm text-forest-500 ml-2">(Loading...)</span>}
          </h3>
          
          <div className="space-y-4">
            {activeSection === 'team' && (
              <>
                {['founder', 'directors', 'advisors', 'members'].map(category => (
                  <div key={category} className="border border-forest-200 rounded-lg p-4">
                    <h4 className="font-semibold text-forest-800 mb-3 capitalize">
                      {category} ({teamData[category]?.length || 0})
                    </h4>
                    {teamData[category]?.length > 0 ? (
                      <div className="space-y-2">
                        {teamData[category].map((member, index) => (
                          <div key={index} className="flex justify-between items-center p-3 bg-forest-50 rounded">
                            <div>
                              <div className="font-medium">{member.display_name}</div>
                              <div className="text-sm text-forest-600">@{member.twitch} • {member.role}</div>
                              {member.bio && <div className="text-xs text-forest-500 mt-1">{member.bio.substring(0, 100)}...</div>}
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setEditingItem({type: 'team', category, index, data: member})}
                                className="btn-enhanced btn-outline text-sm px-3 py-1"
                              >
                                ✏️ Edit
                              </button>
                              <button
                                onClick={() => deleteTeamMember(category, index)}
                                className="btn-enhanced btn-outline text-sm px-3 py-1 text-red-600 border-red-300 hover:bg-red-50"
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-forest-500 text-sm">No {category} added yet.</p>
                    )}
                  </div>
                ))}
              </>
            )}

            {activeSection === 'charity' && (
              <div className="space-y-3">
                {charityData.length > 0 ? (
                  charityData.map((charity, index) => (
                    <div key={index} className="flex justify-between items-start p-4 bg-forest-50 rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium text-forest-800">{charity.name}</div>
                        <div className="text-sm text-forest-600 italic">"{charity.mission}"</div>
                        <div className="text-sm text-forest-500 mt-1">{charity.description.substring(0, 150)}...</div>
                        <div className="text-xs text-forest-500 mt-2">
                          ${charity.amountRaised?.toLocaleString()} raised • Last event: {charity.lastEvent}
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => setEditingItem({type: 'charity', index, data: charity})}
                          className="btn-enhanced btn-outline text-sm px-3 py-1"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => deleteCharity(index)}
                          className="btn-enhanced btn-outline text-sm px-3 py-1 text-red-600 border-red-300 hover:bg-red-50"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-forest-500 text-center py-8">No charities added yet.</p>
                )}
              </div>
            )}

            {activeSection === 'events' && (
              <div className="space-y-3">
                {eventsData.length > 0 ? (
                  eventsData.map((event, index) => (
                    <div key={index} className="flex justify-between items-start p-4 bg-forest-50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="font-medium text-forest-800">{event.title}</div>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            event.status === 'upcoming' ? 'bg-blue-100 text-blue-800' :
                            event.status === 'live' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {event.status}
                          </span>
                          <span className="px-2 py-1 text-xs rounded-full bg-forest-100 text-forest-800">
                            {event.type}
                          </span>
                        </div>
                        <div className="text-sm text-forest-600">
                          {event.date} {event.time && `• ${event.time}`} • Host: {event.host}
                        </div>
                        <div className="text-sm text-forest-500 mt-1">{event.description.substring(0, 150)}...</div>
                        {event.tags && event.tags.length > 0 && (
                          <div className="flex gap-1 mt-2">
                            {event.tags.slice(0, 3).map((tag, tagIndex) => (
                              <span key={tagIndex} className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                                {tag}
                              </span>
                            ))}
                            {event.tags.length > 3 && <span className="text-xs text-forest-500">+{event.tags.length - 3} more</span>}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => setEditingItem({type: 'events', index, data: event})}
                          className="btn-enhanced btn-outline text-sm px-3 py-1"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => deleteEvent(index)}
                          className="btn-enhanced btn-outline text-sm px-3 py-1 text-red-600 border-red-300 hover:bg-red-50"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-forest-500 text-center py-8">No events added yet.</p>
                )}
              </div>
            )}

            {activeSection === 'statistics' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Homepage Statistics */}
                  <div className="border border-forest-200 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-forest-800 mb-4">
                      🏠 Homepage Statistics ("Canopy by the Numbers")
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-forest-700 mb-1">
                          Team Members
                        </label>
                        <input
                          type="text"
                          value={statisticsData.homepage?.teamMembers || ''}
                          onChange={(e) => setStatisticsData(prev => ({
                            ...prev,
                            homepage: { ...prev.homepage, teamMembers: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-forest-300 rounded-md focus:ring-forest-500 focus:border-forest-500"
                          placeholder="7+"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-forest-700 mb-1">
                          Hours Streamed
                        </label>
                        <input
                          type="text"
                          value={statisticsData.homepage?.hoursStreamed || ''}
                          onChange={(e) => setStatisticsData(prev => ({
                            ...prev,
                            homepage: { ...prev.homepage, hoursStreamed: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-forest-300 rounded-md focus:ring-forest-500 focus:border-forest-500"
                          placeholder="13,000+"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-forest-700 mb-1">
                          Raised for Charity
                        </label>
                        <input
                          type="text"
                          value={statisticsData.homepage?.charityRaised || ''}
                          onChange={(e) => setStatisticsData(prev => ({
                            ...prev,
                            homepage: { ...prev.homepage, charityRaised: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-forest-300 rounded-md focus:ring-forest-500 focus:border-forest-500"
                          placeholder="$10K+"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-forest-700 mb-1">
                          Community Events
                        </label>
                        <input
                          type="text"
                          value={statisticsData.homepage?.communityEvents || ''}
                          onChange={(e) => setStatisticsData(prev => ({
                            ...prev,
                            homepage: { ...prev.homepage, communityEvents: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-forest-300 rounded-md focus:ring-forest-500 focus:border-forest-500"
                          placeholder="Weekly"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Charity Statistics */}
                  <div className="border border-forest-200 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-forest-800 mb-4">
                      💝 Charity Page Statistics
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-forest-700 mb-1">
                          Total Charity Goal
                        </label>
                        <input
                          type="text"
                          value={statisticsData.charity?.totalCharityGoal || ''}
                          onChange={(e) => setStatisticsData(prev => ({
                            ...prev,
                            charity: { ...prev.charity, totalCharityGoal: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-forest-300 rounded-md focus:ring-forest-500 focus:border-forest-500"
                          placeholder="$12,000"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-forest-700 mb-1">
                          Next Event Goal
                        </label>
                        <input
                          type="text"
                          value={statisticsData.charity?.nextEventGoal || ''}
                          onChange={(e) => setStatisticsData(prev => ({
                            ...prev,
                            charity: { ...prev.charity, nextEventGoal: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-forest-300 rounded-md focus:ring-forest-500 focus:border-forest-500"
                          placeholder="$5,000"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-forest-700 mb-1">
                          Event Description
                        </label>
                        <input
                          type="text"
                          value={statisticsData.charity?.eventDescription || ''}
                          onChange={(e) => setStatisticsData(prev => ({
                            ...prev,
                            charity: { ...prev.charity, eventDescription: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-forest-300 rounded-md focus:ring-forest-500 focus:border-forest-500"
                          placeholder="Holiday Charity Stream Marathon"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-forest-700 mb-1">
                          Donation Link
                        </label>
                        <input
                          type="text"
                          value={statisticsData.charity?.donationLink || ''}
                          onChange={(e) => setStatisticsData(prev => ({
                            ...prev,
                            charity: { ...prev.charity, donationLink: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-forest-300 rounded-md focus:ring-forest-500 focus:border-forest-500"
                          placeholder="https://tiltify.com/canopy-charity-event"
                        />
                        <p className="text-xs text-forest-500 mt-1">Leave blank to hide the donation button on the Charity page</p>
                      </div>
                    </div>
                  </div>

                  {/* Team Settings */}
                  <div className="border border-forest-200 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-forest-800 mb-4">
                      👥 Team Page Settings
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-forest-700 mb-1">
                          New Membership Prompt
                        </label>
                        <select
                          value={statisticsData.team?.membershipStatus || 'enabled'}
                          onChange={(e) => setStatisticsData(prev => ({
                            ...prev,
                            team: { ...prev.team, membershipStatus: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-forest-300 rounded-md focus:ring-forest-500 focus:border-forest-500"
                        >
                          <option value="enabled">Enabled - "The Canopy is growing! We welcome passionate content creators..."</option>
                          <option value="disabled">Disabled - "We're not taking any submissions at this time..."</option>
                        </select>
                        <p className="text-xs text-forest-500 mt-1">Controls which message is shown in the "Interested in Joining?" section</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <h5 className="font-semibold text-blue-800 mb-2">📝 Usage Notes:</h5>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• <strong>Homepage Statistics:</strong> Display on the home page "Canopy by the Numbers" section</li>
                    <li>• <strong>Charity Statistics:</strong> Used in charity page headers and promotional content</li>
                    <li>• <strong>Format Examples:</strong> Use "7+", "$10K+", "13,000+" for impact numbers</li>
                    <li>• <strong>Save & Deploy:</strong> Click "Save Statistics Changes" then "Deploy to Live Website" to update the live site</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Add/Edit Modal */}
        {(showAddForm || editingItem) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold text-forest-800 mb-4">
                {editingItem ? 'Edit' : 'Add New'} {
                  activeSection === 'team' ? 'Team Member' :
                  activeSection === 'charity' ? 'Charity Partner' : 'Event'
                }
              </h3>
              
              {activeSection === 'team' && (
                <TeamMemberForm 
                  isEdit={!!editingItem}
                  editData={editingItem}
                  onSave={(data) => {
                    if (editingItem) {
                      if (updateTeamMember(editingItem.category, editingItem.index, data)) {
                        setEditingItem(null);
                      }
                    } else {
                      // For new members, we need to ask which category
                      const category = data.category || 'members';
                      if (addTeamMember(category, data)) {
                        setShowAddForm(false);
                      }
                    }
                  }}
                  onCancel={() => {setShowAddForm(false); setEditingItem(null);}}
                />
              )}
              
              {activeSection === 'charity' && (
                <CharityForm 
                  isEdit={!!editingItem}
                  editData={editingItem}
                  onSave={(data) => {
                    if (editingItem) {
                      if (updateCharity(editingItem.index, data)) {
                        setEditingItem(null);
                      }
                    } else {
                      if (addCharity(data)) {
                        setShowAddForm(false);
                      }
                    }
                  }}
                  onCancel={() => {setShowAddForm(false); setEditingItem(null);}}
                />
              )}
              
              {activeSection === 'events' && (
                <EventForm 
                  isEdit={!!editingItem}
                  editData={editingItem}
                  onSave={(data) => {
                    if (editingItem) {
                      if (updateEvent(editingItem.index, data)) {
                        setEditingItem(null);
                      }
                    } else {
                      if (addEvent(data)) {
                        setShowAddForm(false);
                      }
                    }
                  }}
                  onCancel={() => {setShowAddForm(false); setEditingItem(null);}}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Team Member Form Component
const TeamMemberForm = ({ isEdit, editData, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    twitch: editData?.data?.twitch || '',
    display_name: editData?.data?.display_name || '',
    role: editData?.data?.role || (editData?.category === 'founder' ? 'Founder & Owner' : 
          editData?.category === 'directors' ? 'Director' : 'Advisor'),
    website: editData?.data?.website || '',
    bio: editData?.data?.bio || '',
    category: editData?.category || 'members'
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.twitch || formData.twitch.length < 2) {
      newErrors.twitch = 'Twitch username must be at least 2 characters';
    } else if (!/^[a-zA-Z0-9_]{2,25}$/.test(formData.twitch)) {
      newErrors.twitch = 'Twitch username can only contain letters, numbers, and underscores';
    }
    
    if (!formData.display_name || formData.display_name.length < 2) {
      newErrors.display_name = 'Display name must be at least 2 characters';
    }
    
    if (formData.website && !formData.website.match(/^https?:\/\/.+/)) {
      newErrors.website = 'Website must start with http:// or https://';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!isEdit && (
        <div>
          <label className="block text-sm font-medium text-forest-700 mb-1">
            Category
          </label>
          <select
            value={formData.category}
            onChange={(e) => setFormData(prev => ({
              ...prev, 
              category: e.target.value,
              role: e.target.value === 'founder' ? 'Founder & Owner' : 
                    e.target.value === 'directors' ? 'Director' : 
                    e.target.value === 'advisors' ? 'Advisor' : 'Member'
            }))}
            className="w-full px-3 py-2 border border-forest-300 rounded-md focus:ring-forest-500 focus:border-forest-500"
          >
            <option value="founder">Founder</option>
            <option value="directors">Directors</option>
            <option value="advisors">Advisors</option>
            <option value="members">Members</option>
          </select>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-forest-700 mb-1">
            Twitch Username *
          </label>
          <input
            type="text"
            value={formData.twitch}
            onChange={(e) => setFormData(prev => ({...prev, twitch: e.target.value.toLowerCase()}))}
            className={`w-full px-3 py-2 border rounded-md focus:ring-forest-500 focus:border-forest-500 ${
              errors.twitch ? 'border-red-300' : 'border-forest-300'
            }`}
            placeholder="username"
            required
          />
          {errors.twitch && <p className="text-red-600 text-sm mt-1">{errors.twitch}</p>}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-forest-700 mb-1">
            Display Name *
          </label>
          <input
            type="text"
            value={formData.display_name}
            onChange={(e) => setFormData(prev => ({...prev, display_name: e.target.value}))}
            className={`w-full px-3 py-2 border rounded-md focus:ring-forest-500 focus:border-forest-500 ${
              errors.display_name ? 'border-red-300' : 'border-forest-300'
            }`}
            placeholder="Display Name"
            required
          />
          {errors.display_name && <p className="text-red-600 text-sm mt-1">{errors.display_name}</p>}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-forest-700 mb-1">
          Role
        </label>
        <input
          type="text"
          value={formData.role}
          onChange={(e) => setFormData(prev => ({...prev, role: e.target.value}))}
          className="w-full px-3 py-2 border border-forest-300 rounded-md focus:ring-forest-500 focus:border-forest-500"
          placeholder="Role/Title"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-forest-700 mb-1">
          Website (optional)
        </label>
        <input
          type="url"
          value={formData.website}
          onChange={(e) => setFormData(prev => ({...prev, website: e.target.value}))}
          className={`w-full px-3 py-2 border rounded-md focus:ring-forest-500 focus:border-forest-500 ${
            errors.website ? 'border-red-300' : 'border-forest-300'
          }`}
          placeholder="https://example.com"
        />
        {errors.website && <p className="text-red-600 text-sm mt-1">{errors.website}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-forest-700 mb-1">
          Bio (optional)
        </label>
        <textarea
          value={formData.bio}
          onChange={(e) => setFormData(prev => ({...prev, bio: e.target.value}))}
          className="w-full px-3 py-2 border border-forest-300 rounded-md focus:ring-forest-500 focus:border-forest-500"
          rows="4"
          placeholder="Tell us about this team member..."
          maxLength="1000"
        />
        <div className="text-sm text-forest-500 mt-1">
          {formData.bio.length}/1000 characters
        </div>
      </div>

      <div className="flex gap-2 pt-4">
        <button
          type="submit"
          className="btn-enhanced btn-primary"
        >
          {isEdit ? 'Update' : 'Add'} Team Member
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="btn-enhanced btn-outline"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

// Charity Form Component
const CharityForm = ({ isEdit, editData, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: editData?.data?.name || '',
    mission: editData?.data?.mission || '',
    website: editData?.data?.website || '',
    description: editData?.data?.description || '',
    amountRaised: editData?.data?.amountRaised || 0,
    lastEvent: editData?.data?.lastEvent || ''
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name || formData.name.length < 2) {
      newErrors.name = 'Organization name must be at least 2 characters';
    }
    
    if (!formData.mission || formData.mission.length < 10) {
      newErrors.mission = 'Mission statement must be at least 10 characters';
    }
    
    if (!formData.description || formData.description.length < 20) {
      newErrors.description = 'Description must be at least 20 characters';
    }
    
    if (formData.website && !formData.website.match(/^https?:\/\/.+/)) {
      newErrors.website = 'Website must start with http:// or https://';
    }
    
    if (formData.amountRaised < 0) {
      newErrors.amountRaised = 'Amount raised cannot be negative';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-forest-700 mb-1">
          Organization Name *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
          className={`w-full px-3 py-2 border rounded-md focus:ring-forest-500 focus:border-forest-500 ${
            errors.name ? 'border-red-300' : 'border-forest-300'
          }`}
          placeholder="St. Jude Children's Research Hospital"
          required
        />
        {errors.name && <p className="text-red-600 text-sm mt-1">{errors.name}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-forest-700 mb-1">
          Mission Statement *
        </label>
        <input
          type="text"
          value={formData.mission}
          onChange={(e) => setFormData(prev => ({...prev, mission: e.target.value}))}
          className={`w-full px-3 py-2 border rounded-md focus:ring-forest-500 focus:border-forest-500 ${
            errors.mission ? 'border-red-300' : 'border-forest-300'
          }`}
          placeholder="Finding cures. Saving children."
          required
        />
        {errors.mission && <p className="text-red-600 text-sm mt-1">{errors.mission}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-forest-700 mb-1">
          Website
        </label>
        <input
          type="url"
          value={formData.website}
          onChange={(e) => setFormData(prev => ({...prev, website: e.target.value}))}
          className={`w-full px-3 py-2 border rounded-md focus:ring-forest-500 focus:border-forest-500 ${
            errors.website ? 'border-red-300' : 'border-forest-300'
          }`}
          placeholder="https://www.stjude.org/"
          required
        />
        {errors.website && <p className="text-red-600 text-sm mt-1">{errors.website}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-forest-700 mb-1">
          Description *
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
          className={`w-full px-3 py-2 border rounded-md focus:ring-forest-500 focus:border-forest-500 ${
            errors.description ? 'border-red-300' : 'border-forest-300'
          }`}
          rows="4"
          placeholder="Longer description of the charity and why we support them"
          required
          maxLength="1000"
        />
        {errors.description && <p className="text-red-600 text-sm mt-1">{errors.description}</p>}
        <div className="text-sm text-forest-500 mt-1">
          {formData.description.length}/1000 characters
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-forest-700 mb-1">
            Amount Raised
          </label>
          <input
            type="number"
            value={formData.amountRaised}
            onChange={(e) => setFormData(prev => ({...prev, amountRaised: parseInt(e.target.value) || 0}))}
            className={`w-full px-3 py-2 border rounded-md focus:ring-forest-500 focus:border-forest-500 ${
              errors.amountRaised ? 'border-red-300' : 'border-forest-300'
            }`}
            min="0"
            placeholder="5000"
          />
          {errors.amountRaised && <p className="text-red-600 text-sm mt-1">{errors.amountRaised}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-forest-700 mb-1">
            Last Event
          </label>
          <input
            type="text"
            value={formData.lastEvent}
            onChange={(e) => setFormData(prev => ({...prev, lastEvent: e.target.value}))}
            className="w-full px-3 py-2 border border-forest-300 rounded-md focus:ring-forest-500 focus:border-forest-500"
            placeholder="St. Jude Play Live Game Day 2024"
          />
        </div>
      </div>

      <div className="flex gap-2 pt-4">
        <button
          type="submit"
          className="btn-enhanced btn-primary"
        >
          {isEdit ? 'Update' : 'Add'} Charity
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="btn-enhanced btn-outline"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

// Date helper functions (moved outside component to be reusable)
const formatDateForDisplay = (dateStr) => {
  if (!dateStr) return '';
  // Parse the date string directly to avoid timezone issues
  const [year, month, day] = dateStr.split('-').map(num => parseInt(num));
  const date = new Date(year, month - 1, day); // Month is 0-indexed
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

const convertToDatePickerFormat = (displayDate) => {
  if (!displayDate) return '';
  try {
    // Try to parse various date formats
    let date;

    // If it's already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(displayDate)) {
      return displayDate;
    }

    // Parse common display formats like "Dec 15, 2024" or "15 Dec 2024"
    date = new Date(displayDate);
    if (isNaN(date.getTime())) {
      return '';
    }

    // Format as YYYY-MM-DD without timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
};

// Event Form Component
const EventForm = ({ isEdit, editData, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    startDate: editData?.data?.startDate || editData?.data?.date || '',
    endDate: editData?.data?.endDate || '',
    time: editData?.data?.time || '',
    title: editData?.data?.title || '',
    description: editData?.data?.description || '',
    type: editData?.data?.type || 'community',
    host: editData?.data?.host || '',
    participants: editData?.data?.participants || '',
    status: editData?.data?.status || 'upcoming',
    link: editData?.data?.link || '',
    tags: Array.isArray(editData?.data?.tags) ? editData.data.tags : []
  });
  const [errors, setErrors] = useState({});

  const eventTypes = ['charity', 'raid-train', 'game-night', 'tournament', 'community'];
  const eventStatuses = ['upcoming', 'live', 'completed'];
  
  // Timezone options (common US timezones for streaming)
  const timezones = [
    { value: 'EST', label: 'Eastern (EST)' },
    { value: 'CST', label: 'Central (CST)' },
    { value: 'MST', label: 'Mountain (MST)' },
    { value: 'PST', label: 'Pacific (PST)' },
    { value: 'GMT', label: 'GMT' },
    { value: 'UTC', label: 'UTC' }
  ];


  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title || formData.title.length < 3) {
      newErrors.title = 'Title must be at least 3 characters';
    }
    
    if (!formData.description || formData.description.length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }
    
    if (!formData.host || formData.host.length < 2) {
      newErrors.host = 'Host must be at least 2 characters';
    }
    
    if (!formData.participants || formData.participants.length < 2) {
      newErrors.participants = 'Participants must be at least 2 characters';
    }
    
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    
    if (formData.startDate && formData.endDate) {
      // Convert both dates to YYYY-MM-DD format for proper comparison
      const startDateStr = convertToDatePickerFormat(formData.startDate) || formData.startDate;
      const endDateStr = convertToDatePickerFormat(formData.endDate) || formData.endDate;

      if (startDateStr && endDateStr && endDateStr < startDateStr) {
        newErrors.endDate = 'End date must be after start date';
      }
    }
    
    if (formData.link && formData.link.length > 0 && !formData.link.match(/^https?:\/\/.+/)) {
      newErrors.link = 'Link must start with http:// or https://';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Set date for backward compatibility
      const dataToSave = {
        ...formData,
        date: formData.startDate, // Keep date field for backward compatibility
        tags: formData.tags
      };
      onSave(dataToSave);
    }
  };

  const handleDateChange = (field, e) => {
    const dateValue = e.target.value; // This is in YYYY-MM-DD format from date input
    if (dateValue) {
      const displayDate = formatDateForDisplay(dateValue);
      setFormData(prev => {
        const newData = {...prev, [field]: displayDate};

        // Auto-suggest end date when start date is set and end date is empty
        if (field === 'startDate' && !prev.endDate) {
          const [year, month, day] = dateValue.split('-').map(num => parseInt(num));
          const startDate = new Date(year, month - 1, day);
          const nextDay = new Date(startDate);
          nextDay.setDate(startDate.getDate() + 1);

          const nextYear = nextDay.getFullYear();
          const nextMonth = String(nextDay.getMonth() + 1).padStart(2, '0');
          const nextDayStr = String(nextDay.getDate()).padStart(2, '0');
          newData.endDate = formatDateForDisplay(`${nextYear}-${nextMonth}-${nextDayStr}`);
        }

        return newData;
      });
    } else {
      setFormData(prev => ({...prev, [field]: ''}));
    }
  };

  const handleTimeChange = (field, value) => {
    const currentTime = formData.time || '';
    let timeComponents = currentTime.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?\s*(.*)$/i) || ['', '12', '00', 'PM', 'EST'];
    
    if (field === 'hour') {
      timeComponents[1] = value;
    } else if (field === 'minute') {
      timeComponents[2] = value;
    } else if (field === 'period') {
      timeComponents[3] = value;
    } else if (field === 'timezone') {
      timeComponents[4] = value;
    }
    
    const newTime = `${timeComponents[1]}:${timeComponents[2]} ${timeComponents[3]} ${timeComponents[4]}`;
    setFormData(prev => ({...prev, time: newTime}));
  };

  // Parse current time for the time picker components
  const parseCurrentTime = () => {
    const timeMatch = formData.time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?\s*(.*)$/i);
    if (timeMatch) {
      return {
        hour: timeMatch[1] || '7',
        minute: timeMatch[2] || '00',
        period: timeMatch[3] || 'PM',
        timezone: timeMatch[4] || 'EST'
      };
    }
    return { hour: '7', minute: '00', period: 'PM', timezone: 'EST' };
  };

  const timeComponents = parseCurrentTime();

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-forest-700 mb-1">
            Start Date *
          </label>
          <input
            type="date"
            value={convertToDatePickerFormat(formData.startDate)}
            onChange={(e) => handleDateChange('startDate', e)}
            className={`w-full px-3 py-2 border rounded-md focus:ring-forest-500 focus:border-forest-500 ${
              errors.startDate ? 'border-red-300' : 'border-forest-300'
            }`}
            required
          />
          {errors.startDate && <p className="text-red-600 text-sm mt-1">{errors.startDate}</p>}
          {formData.startDate && (
            <p className="text-sm text-forest-600 mt-1">Display: {formData.startDate}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-forest-700 mb-1">
            End Date
          </label>
          <input
            type="date"
            value={convertToDatePickerFormat(formData.endDate)}
            onChange={(e) => handleDateChange('endDate', e)}
            className={`w-full px-3 py-2 border rounded-md focus:ring-forest-500 focus:border-forest-500 ${
              errors.endDate ? 'border-red-300' : 'border-forest-300'
            }`}
          />
          {errors.endDate && <p className="text-red-600 text-sm mt-1">{errors.endDate}</p>}
          {formData.endDate && (
            <p className="text-sm text-forest-600 mt-1">Display: {formData.endDate}</p>
          )}
          <p className="text-xs text-forest-500 mt-1">
            {formData.startDate && !formData.endDate ?
              'Auto-suggested: next day after start date' :
              'Optional: will default to next day after start date if not specified'
            }
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-forest-700 mb-1">
            Event Time
          </label>
          <div className="grid grid-cols-4 gap-2">
            <select
              value={timeComponents.hour}
              onChange={(e) => handleTimeChange('hour', e.target.value)}
              className="px-2 py-2 border border-forest-300 rounded-md focus:ring-forest-500 focus:border-forest-500 text-sm"
            >
              {Array.from({length: 12}, (_, i) => i + 1).map(hour => (
                <option key={hour} value={hour}>{hour}</option>
              ))}
            </select>
            <select
              value={timeComponents.minute}
              onChange={(e) => handleTimeChange('minute', e.target.value)}
              className="px-2 py-2 border border-forest-300 rounded-md focus:ring-forest-500 focus:border-forest-500 text-sm"
            >
              {['00', '15', '30', '45'].map(minute => (
                <option key={minute} value={minute}>{minute}</option>
              ))}
            </select>
            <select
              value={timeComponents.period}
              onChange={(e) => handleTimeChange('period', e.target.value)}
              className="px-2 py-2 border border-forest-300 rounded-md focus:ring-forest-500 focus:border-forest-500 text-sm"
            >
              <option value="AM">AM</option>
              <option value="PM">PM</option>
            </select>
            <select
              value={timeComponents.timezone}
              onChange={(e) => handleTimeChange('timezone', e.target.value)}
              className="px-2 py-2 border border-forest-300 rounded-md focus:ring-forest-500 focus:border-forest-500 text-sm"
            >
              {timezones.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.value}</option>
              ))}
            </select>
          </div>
          {formData.time && (
            <p className="text-sm text-forest-600 mt-1">Display: {formData.time}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-forest-700 mb-1">
          Title *
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
          className={`w-full px-3 py-2 border rounded-md focus:ring-forest-500 focus:border-forest-500 ${
            errors.title ? 'border-red-300' : 'border-forest-300'
          }`}
          placeholder="Holiday Charity Stream"
          required
        />
        {errors.title && <p className="text-red-600 text-sm mt-1">{errors.title}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-forest-700 mb-1">
          Description *
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
          className={`w-full px-3 py-2 border rounded-md focus:ring-forest-500 focus:border-forest-500 ${
            errors.description ? 'border-red-300' : 'border-forest-300'
          }`}
          rows="3"
          placeholder="Join us for our annual holiday charity fundraising stream!"
          required
          maxLength="1000"
        />
        {errors.description && <p className="text-red-600 text-sm mt-1">{errors.description}</p>}
        <div className="text-sm text-forest-500 mt-1">
          {formData.description.length}/1000 characters
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-forest-700 mb-1">
            Type
          </label>
          <select
            value={formData.type}
            onChange={(e) => setFormData(prev => ({...prev, type: e.target.value}))}
            className="w-full px-3 py-2 border border-forest-300 rounded-md focus:ring-forest-500 focus:border-forest-500"
          >
            {eventTypes.map(type => (
              <option key={type} value={type}>
                {type.replace('-', ' ').toUpperCase()}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-forest-700 mb-1">
            Status
          </label>
          <select
            value={formData.status}
            onChange={(e) => setFormData(prev => ({...prev, status: e.target.value}))}
            className="w-full px-3 py-2 border border-forest-300 rounded-md focus:ring-forest-500 focus:border-forest-500"
          >
            {eventStatuses.map(status => (
              <option key={status} value={status}>
                {status.toUpperCase()}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-forest-700 mb-1">
            Link (optional)
          </label>
          <input
            type="url"
            value={formData.link}
            onChange={(e) => setFormData(prev => ({...prev, link: e.target.value}))}
            className={`w-full px-3 py-2 border rounded-md focus:ring-forest-500 focus:border-forest-500 ${
              errors.link ? 'border-red-300' : 'border-forest-300'
            }`}
            placeholder="https://twitch.tv/channel"
          />
          {errors.link && <p className="text-red-600 text-sm mt-1">{errors.link}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-forest-700 mb-1">
            Host *
          </label>
          <input
            type="text"
            value={formData.host}
            onChange={(e) => setFormData(prev => ({...prev, host: e.target.value}))}
            className={`w-full px-3 py-2 border rounded-md focus:ring-forest-500 focus:border-forest-500 ${
              errors.host ? 'border-red-300' : 'border-forest-300'
            }`}
            placeholder="The Canopy Team"
            required
          />
          {errors.host && <p className="text-red-600 text-sm mt-1">{errors.host}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-forest-700 mb-1">
            Participants *
          </label>
          <input
            type="text"
            value={formData.participants}
            onChange={(e) => setFormData(prev => ({...prev, participants: e.target.value}))}
            className={`w-full px-3 py-2 border rounded-md focus:ring-forest-500 focus:border-forest-500 ${
              errors.participants ? 'border-red-300' : 'border-forest-300'
            }`}
            placeholder="All Team Members"
            required
          />
          {errors.participants && <p className="text-red-600 text-sm mt-1">{errors.participants}</p>}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-forest-700 mb-2">
          Event Tags
        </label>
        <div className="space-y-2">
          {['charity', 'raid-train', 'game-night', 'tournament', 'community'].map(tag => (
            <label key={tag} className="flex items-center">
              <input
                type="checkbox"
                checked={formData.tags.includes(tag)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData(prev => ({...prev, tags: [...prev.tags, tag]}));
                  } else {
                    setFormData(prev => ({...prev, tags: prev.tags.filter(t => t !== tag)}));
                  }
                }}
                className="h-4 w-4 text-forest-600 focus:ring-forest-500 border-forest-300 rounded"
              />
              <span className="ml-2 text-sm text-forest-700 capitalize">{tag.replace('-', ' ')}</span>
            </label>
          ))}
        </div>
        <div className="text-sm text-forest-500 mt-1">
          Select one or more event types
        </div>
      </div>

      <div className="flex gap-2 pt-4">
        <button
          type="submit"
          className="btn-enhanced btn-primary"
          disabled={Object.keys(errors).length > 0}
        >
          {isEdit ? 'Update' : 'Add'} Event
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="btn-enhanced btn-outline"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default FullEditor;